import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import i18n, { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface DepartmentDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    departmentKey: string;
    group_Id: number;
    referenceYear_Id: number;
}

export interface DepartmentState {
    departments: DepartmentDto[];
    department: DepartmentDto;
    gettingDepartments: boolean;
    gettingDepartment: boolean;
    archivingDepartment: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const blankDepartmentDto: DepartmentDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
    departmentKey: '',
    group_Id: 0,
    referenceYear_Id: 0,
};

const initialState: DepartmentState = {
    departments: [],
    department: blankDepartmentDto,
    gettingDepartments: false,
    gettingDepartment: false,
    archivingDepartment: false,
    status: 'idle',
    error: undefined,
};

export const departmentSlice = createSlice({
    name: 'departments',
    initialState,
    reducers: {
        clearDepartments: (state) => {
            state.departments = [];
            state.department = blankDepartmentDto;
        },
        updateDepartment: (state, param) => {
            const { payload } = param;
            state.department = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getDepartments.pending, (state, action) => {
                state.gettingDepartments = true;
                state.status = 'loading';
            })
            .addCase(getDepartments.fulfilled, (state, action) => {
                state.gettingDepartments = false;
                state.departments = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getDepartments.rejected, (state, action) => {
                state.gettingDepartments = false;
                state.departments = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getDepartment.pending, (state, action) => {
                state.gettingDepartment = true;
                state.status = 'loading';
            })
            .addCase(getDepartment.fulfilled, (state, action) => {
                state.gettingDepartment = false;
                state.department = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getDepartment.rejected, (state, action) => {
                state.gettingDepartment = false;
                state.department = blankDepartmentDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveDepartment.pending, (state, action) => {
                state.archivingDepartment = true;
                state.status = 'loading';
            })
            .addCase(archiveDepartment.fulfilled, (state, action) => {
                state.archivingDepartment = false;
                state.departments = state.departments.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveDepartment.rejected, (state, action) => {
                state.archivingDepartment = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearDepartments } = departmentSlice.actions;
export const { updateDepartment } = departmentSlice.actions;

export const getDepartments = createAsyncThunk('departments/getDepartments', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/departments`);

    return response.data;
});

export const getDepartment = createAsyncThunk('departments/getDepartment', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/departments/${id}`);

    return response.data;
});

export const archiveDepartment = createAsyncThunk('departments/archiveDepartment', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/departments/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedDepartement'),
        })
    );
    return response.data;
});

export const saveDepartment = createAsyncThunk('departments/saveDepartment', async (departmentToSave: DepartmentDto, thunkAPI) => {
    if (departmentToSave.id === 0) {
        return axios
            .post(`/api/synergy/departments/create`, departmentToSave)
            .then((data) => {
                thunkAPI.dispatch(
                    showMessage({
                        type: 'info',
                        message: getAppText('createDepartement'),
                    })
                );
                return data;
            })
            .catch(async (error) => {
                const message = await error.response.data.text();
                thunkAPI.dispatch(
                    showMessage({
                        type: 'error',
                        message: i18n.t(message),
                    })
                );
            });
    } else {
        return axios
            .put(`/api/synergy/departments/put`, departmentToSave)
            .then((data) => {
                thunkAPI.dispatch(
                    showMessage({
                        type: 'info',
                        message: getAppText('saveDepartement'),
                    })
                );
                return data;
            })
            .catch(async (error) => {
                const message = await error.response.data.text();
                thunkAPI.dispatch(
                    showMessage({
                        type: 'error',
                        message: i18n.t(message),
                    })
                );
            });
    }
});
